import { useEffect, useState } from "react";
import {
  FormProvider,
  useForm,
  Controller,
  useFieldArray,
} from "react-hook-form";
import { useAxiosInstance } from "../../../../services/Interceptor";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { useDispatch } from "react-redux";
import {
  registerAccountStore,
  clearRegisterData,
} from "../../../../store/slices/accountSlice";
import { positions, sports, states } from "../../../../utils/PositionStateData";
import LoadingSpinner from "../../../../components/LoadingSpinner";
// import { ErrorMessages } from "../../../../components/errorMessages/errorMessages";
import { InputText } from "./inputText";
import { InputEmail } from "./inputEmail";
import { InputNumber } from "./inputNumber";
import { InputSelect } from "./inputSelect";
import { CheckIsEmailExist } from "../../../../services/AccountService";
import { GetAdminPrice } from "../../../../services/EventsService";
import Multiselect from "multiselect-react-dropdown";

export const RegisterAthlete = () => {
  const methods = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      age: "",
      gpa: "",
      highSchool: "",
      expectedYearOfGraduation: "",
      primaryPosition: "",
      secondaryPosition: "",
      sportsParticipation: "",
      city: "",
      state: "",
      universityOfInteres: "",
      academicDegreeOfInteres: "",
      role: "athlete",
      athletePhoneNumber: "",
      parentPhoneNumber: "",
      varsityYears: "",
      offersReceived: "no",
      athleteOffers: [],
      shouldUnregister: true,
    },
  });
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    control,
  } = methods;

  const password = watch("password");

  const apiInstance = useAxiosInstance();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [athleteMonthlyPrice, setAthlleteMonthlyPrice] = useState(0);
  const [athleteAnnualPrice, setAthlleteAnnualPrice] = useState(0);

  const primaryPositions = positions.filter((p) => p !== "None");

  const {
    fields: offerFields,
    append,
    remove,
    replace,
  } = useFieldArray({
    control,
    name: "athleteOffers",
  });

  const offersReceived = watch("offersReceived");
  useEffect(() => {
    const fetchPrices = async () => {
      const adminPrices = await GetAdminPrice(apiInstance);
      const athletePricing = adminPrices?.data?.find(
        (option) => option.role.toLowerCase() === "athlete",
      );
      setAthlleteMonthlyPrice(athletePricing?.pricingOptions?.monthlyPrice);
      setAthlleteAnnualPrice(athletePricing?.pricingOptions?.annualPrice);
    };
    fetchPrices();
  }, [apiInstance]);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  const checkIsEmailExist = async (email) => {
    try {
      const response = await CheckIsEmailExist(apiInstance, email);
      if (response?.data.data === false) {
        toast.warning("Email ID is already registered!", {
          position: "top-center",
        });
        return true;
      }
      return false;
    } catch (error) {
      toast.error("Failed to validate email address.", {
        position: "top-center",
      });
      return true;
    }
  };

  const onSubmit = async (data) => {
    if (data.password !== data.confirmPassword) {
      toast.error("Passwords do not match!", { position: "top-center" });
      return;
    }

    const isEmailExist = await checkIsEmailExist(data.email);
    if (isEmailExist) {
      setLoading(false);
      return;
    }
    const stripPosition = (position) => {
      if (!position) return "";
      return position.includes("(") ? position.split("(")[0].trim() : position;
    };

    const payload = {
      ...data,
      primaryPosition: stripPosition(
        data.primaryPosition === "None" ? "" : data.primaryPosition,
      ),
      secondaryPosition: stripPosition(
        data.secondaryPosition === "None" ? "" : data.secondaryPosition,
      ),
      sportsParticipation: Array.isArray(data.sportsParticipation)
        ? data.sportsParticipation.join(", ")
        : "",
    };

    setLoading(true);
    try {
      if (athleteMonthlyPrice <= 0 && athleteAnnualPrice <= 0) {
        dispatch(clearRegisterData());
        dispatch(registerAccountStore(payload));
        localStorage.clear();
        localStorage.setItem("registeredAccount", JSON.stringify(payload));
        setLoading(false);
        toast.success("Please complete the Payment.", {
          position: "top-right",
        });
        reset();
        setTimeout(() => {
          window.location.href = "/register/athletSubscription";
        }, 2000);
      } else {
        dispatch(clearRegisterData());
        dispatch(registerAccountStore(payload));
        localStorage.clear();
        localStorage.setItem("registeredAccount", JSON.stringify(payload));
        setLoading(false);
        toast.success("Please complete the Payment.", {
          position: "top-right",
        });
        reset();
        setTimeout(() => {
          window.location.href = "/register/subscription";
        }, 2000);
      }
    } catch (error) {
      toast.error(`Error Registering Athlete: ${error.message || error}`, {
        position: "top-right",
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    if (offersReceived === "yes" && offerFields.length === 0) {
      append({ schoolName: "", division: "", offerYear: "" });
    }
    if (offersReceived === "no" && offerFields.length > 0) {
      // One-shot clear to avoid render storms
      replace([]);
    }
  }, [offersReceived, offerFields.length, append, replace]);

  return (
    <FormProvider {...methods}>
      <form
        className="container mx-auto px-4"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="px-2 mb-20">
          <div className="grid sm:grid-cols-2 gap-1 xl:gap-8 lg:gap-8">
            <InputText label={"First Name"} name={"firstName"} />
            <InputText label={"Last Name"} name={"lastName"} />
          </div>
          <InputEmail />
          <div className="form-field mt-4">
            <label htmlFor="password">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                {...register("password", {
                  required: "Password is required",
                  minLength: {
                    value: 8,
                    message: "Password must be at least 8 characters",
                  },
                })}
                placeholder="Password"
                className="px-4 py-2 w-full border rounded"
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500"
              >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </button>
            </div>
            {errors.password && (
              <span className="text-red-500">{errors.password.message}</span>
            )}
          </div>

          <div className="form-field mt-4">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                {...register("confirmPassword", {
                  required: "Confirm Password is required",
                  validate: (value) =>
                    value === password || "Passwords do not match",
                })}
                placeholder="Confirm Password"
                className="px-4 py-2 w-full border rounded"
              />
              <button
                type="button"
                onClick={toggleConfirmPasswordVisibility}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500"
              >
                <FontAwesomeIcon
                  icon={showConfirmPassword ? faEyeSlash : faEye}
                />
              </button>
            </div>
            {errors.confirmPassword && (
              <span className="text-red-500">
                {errors.confirmPassword.message}
              </span>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-1 xl:gap-8 lg:gap-8">
            <InputNumber
              label={"Age"}
              max={30}
              min={11}
              step={1}
              name={"age"}
            />
            <InputText label={"GPA"} name={"gpa"} />
          </div>
          <div className="grid sm:grid-cols-2 gap-1 xl:gap-8 lg:gap-8">
            <InputText label={"High School"} name={"highSchool"} />
            <InputNumber
              label={"Expected Year of Graduation"}
              name={"expectedYearOfGraduation"}
              max={2035}
              min={2015}
              step={1}
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-1 xl:gap-8 lg:gap-8">
            <div className="form-field mt-4">
              <label>Athlete Phone Number</label>
              <input
                type="tel"
                inputMode="numeric"
                pattern="[0-9]{10}"
                placeholder="Athlete Phone Number"
                maxLength={10}
                {...register("athletePhoneNumber", {
                  required: "Athlete Phone Number is required",
                  pattern: {
                    value: /^[0-9]{10}$/,
                    message: "Enter a valid 10-digit phone number",
                  },
                })}
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onInput={(e) => {
                  e.target.value = e.target.value.replace(/\D/g, "");
                }}
              />
            </div>

            <div className="form-field mt-4">
              <label>Parent Phone Number</label>
              <input
                type="tel"
                inputMode="numeric"
                pattern="[0-9]{10}"
                placeholder="Parent Phone Number"
                maxLength={10}
                {...register("parentPhoneNumber", {
                  required: "Parent Phone Number is required",
                  pattern: {
                    value: /^[0-9]{10}$/,
                    message: "Enter a valid 10-digit phone number",
                  },
                })}
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onInput={(e) => {
                  e.target.value = e.target.value.replace(/\D/g, "");
                }}
              />
            </div>
          </div>
          <InputSelect
            label={"Primary Position"}
            name={"primaryPosition"}
            options={primaryPositions}
            placeholder="Select Primary Position"
          />
          <InputSelect
            label={"Secondary Position"}
            name={"secondaryPosition"}
            options={positions}
            placeholder="Select Secondary Position"
            required={false}
          />
          <div className="form-field mt-4">
            <label
              htmlFor="sportsParticipation"
              className="block text-base text-gray-900"
            >
              Sports Participation
            </label>
            <Controller
              control={control}
              name="sportsParticipation"
              rules={{ required: "At least one sport is required" }}
              defaultValue={[]}
              render={({ field: { onChange, value } }) => {
                // Convert string array into object array
                const sportsOptions = sports.map((sport, index) => ({
                  id: index,
                  name: sport,
                }));

                return (
                  <Multiselect
                    options={sportsOptions}
                    displayValue="name"
                    selectedValues={sportsOptions.filter((s) =>
                      value?.includes(s.name),
                    )}
                    onSelect={(selectedList) => {
                      const selected = selectedList.map((item) => item.name);
                      onChange(selected);
                    }}
                    onRemove={(selectedList) => {
                      const selected = selectedList.map((item) => item.name);
                      onChange(selected);
                    }}
                    placeholder="Select Sports"
                    className="bg-gray-50 border border-gray-300 text-md rounded-md form-select appearance-none bg-no-repeat text-gray-400"
                  />
                );
              }}
            />

            {errors.sportsParticipation && (
              <span className="text-red-500">
                {errors.sportsParticipation.message}
              </span>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-1 xl:gap-8 lg:gap-8">
            <InputText label={"City"} name={"city"} />
            <InputSelect
              label={"State"}
              name={"state"}
              options={states}
              placeholder="Select a State"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-1 xl:gap-8 lg:gap-8">
            <InputText
              label={"Universities You are Most Interested in Playing For"}
              name={"universityOfInteres"}
              required={false}
            />
            <InputText
              label={"Academic Degree of Interest"}
              name={"academicDegreeOfInteres"}
              required={false}
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-1 xl:gap-8 lg:gap-8 mt-4">
            <div className="form-field">
              <label htmlFor="athletePhone">Years of Varsity Experience</label>
              <input
                type="tel"
                id="varsityYears"
                inputMode="numeric"
                placeholder="Years of Varsity Experience"
                maxLength={15}
                {...register("varsityYears", {
                  required: "Years of Varsity Experience is required",
                })}
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onInput={(e) => {
                  e.target.value = e.target.value.replace(/\D/g, "");
                }}
              />
              {errors.varsityYears && (
                <span className="text-red-500 text-sm">
                  {errors.varsityYears?.message}
                </span>
              )}
            </div>
          </div>
          {/* Have you received any offers? */}
          <div className="form-field mt-6">
            <label className="block mb-2 font-semibold">
              Have you received any offers?
            </label>

            <div className="flex items-center gap-6">
              {/* YES radio */}
              {(() => {
                const offersReceivedRegister = register("offersReceived");
                return (
                  <>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        value="yes"
                        className="mr-2"
                        {...offersReceivedRegister}
                        onChange={(e) => offersReceivedRegister.onChange(e)}
                      />
                      <span>Yes</span>
                    </label>

                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        value="no"
                        className="mr-2"
                        {...offersReceivedRegister}
                        onChange={(e) => offersReceivedRegister.onChange(e)}
                      />
                      <span>No</span>
                    </label>
                  </>
                );
              })()}
            </div>
          </div>

          {/* Offer rows (only if they answered Yes) */}
          {offersReceived === "yes" && (
            <div className="mt-4 space-y-3">
              {offerFields.map((field, index) => (
                <div
                  key={field.id}
                  className="grid sm:grid-cols-4 gap-3 items-end border border-gray-200 rounded-md p-3"
                >
                  {/* School */}
                  <div className="form-field">
                    <label className="block mb-1">School</label>
                    <input
                      placeholder="School"
                      {...register(`athleteOffers.${index}.schoolName`, {
                        required: "School is required",
                      })}
                    />
                    {errors.athleteOffers?.[index]?.schoolName && (
                      <span className="text-red-500 text-sm">
                        {errors.athleteOffers[index].schoolName?.message}
                      </span>
                    )}
                  </div>

                  {/* Division */}
                  <div className="form-field">
                    <label className="block mb-1">Division</label>
                    <select
                      defaultValue=""
                      {...register(`athleteOffers.${index}.division`, {
                        required: "Division is required",
                      })}
                      className="bg-gray-50 border border-gray-300 rounded-md w-full"
                    >
                      <option value="">Select</option>
                      <option value="D1">D1</option>
                      <option value="D2">D2</option>
                      <option value="D3">D3</option>
                    </select>
                    {errors.athleteOffers?.[index]?.division && (
                      <span className="text-red-500 text-sm">
                        {errors.athleteOffers[index].division?.message}
                      </span>
                    )}
                  </div>

                  {/* Year */}
                  <div className="form-field">
                    <label className="block mb-1">Year</label>
                    <input
                      type="number"
                      placeholder="offer Year"
                      {...register(`athleteOffers.${index}.offerYear`, {
                        required: "Year is required",
                        validate: (v) =>
                          (/^\d{4}$/.test(String(v)) && Number(v) >= 1900) ||
                          "Enter a valid 4-digit year",
                      })}
                    />
                    {errors.athleteOffers?.[index]?.offerYear && (
                      <span className="text-red-500 text-sm">
                        {errors.athleteOffers[index].offerYear?.message}
                      </span>
                    )}
                  </div>

                  {/* Remove row (X) */}
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="text-red-600 font-bold px-2 py-1 border border-red-400 rounded hover:bg-red-50"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={() =>
                  append({ schoolName: "", division: "", offerYear: "" })
                }
                className="mt-2 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium bg-white hover:bg-gray-50"
              >
                Add another
              </button>
            </div>
          )}
          <div className="form-field mt-4">{loading && <LoadingSpinner />}</div>
          <button
            type="submit"
            className="bg-red-500 text-white uppercase rounded px-8 py-2 text-center mt-4 hover:bg-red-700 cursor-pointer"
          >
            Submit
          </button>
        </div>
      </form>
      <ToastContainer />
    </FormProvider>
  );
};
